import React, { useState, useEffect, useRef, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './PhonemicPerception.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const IMAGE_MAP = {
  'бочка': 'barrel', 'дом': 'house', 'дым': 'smoke', 'кит': 'whale', 'коза': 'goat',
  'коса': 'braid', 'кот': 'cat', 'кошка': 'cat', 'лук': 'onion', 'люк': 'hatch',
  'миска': 'bowl', 'мишка': 'bear', 'мошка': 'midge', 'мышка': 'mouse', 
  'плач': 'cry', 'плащ': 'raincoat', 'почка': 'burgeon', 'уточка': 'duck', 'удочка': 'fishingrod', 'чаша': 'big_bowl', 
  'чаща': 'thicket', 'жаба': 'toad', 'жук': 'beetle', 'зуб': 'tooth', 'суп': 'soup', 'шапка': 'hat', 'шуба': 'fur_coat', 'сук': 'branch',
};

const PAIRS_BY_AGE = {
  '4': [
    { pair: ['кот', 'кит'], contrast: '[о]–[и]' },
    { pair: ['дом', 'дым'], contrast: '[о]–[ы]' },
    { pair: ['уточка', 'удочка'], contrast: '[т]–[д]' },
    { pair: ['коса', 'коза'], contrast: '[с]–[з]' },
    { pair: ['мишка', 'миска'], contrast: '[ш]–[с]' },
  ],
  '5': [
    { pair: ['мышка', 'мишка'], contrast: '[ы]–[и]' },
    { pair: ['почка', 'бочка'], contrast: '[п]–[б]' },
    { pair: ['лук', 'люк'], contrast: '[л]–[л\']' },
    { pair: ['плач', 'плащ'], contrast: '[ч]–[щ]' },
    { pair: ['мышка', 'мошка'], contrast: '[ы]–[о]' },
  ],
  '6': [
    { pair: ['чаша', 'чаща'], contrast: '[ш]–[щ]' },
    { pair: ['суп', 'шуба'], contrast: '[с]–[ш]' },
    { pair: ['зуб', 'суп'], contrast: '[з]–[с]' },
    { pair: ['зуб', 'жук'], contrast: '[з]–[ж]' },
    { pair: ['сук', 'жук'], contrast: '[с]–[ж]' },
    { pair: ['жаба', 'шапка'], contrast: '[ж]–[ш]' },
  ],
  '7-9': [
    { pair: ['чаша', 'чаща'], contrast: '[ш]–[щ]' },
    { pair: ['суп', 'шуба'], contrast: '[с]–[ш]' },
    { pair: ['зуб', 'суп'], contrast: '[з]–[с]' },
    { pair: ['зуб', 'жук'], contrast: '[з]–[ж]' },
    { pair: ['сук', 'жук'], contrast: '[с]–[ж]' },
    { pair: ['жаба', 'шапка'], contrast: '[ж]–[ш]' },
  ],
  '10-12': [
    { pair: ['чаша', 'чаща'], contrast: '[ш]–[щ]' },
    { pair: ['суп', 'шуба'], contrast: '[с]–[ш]' },
    { pair: ['зуб', 'суп'], contrast: '[з]–[с]' },
    { pair: ['зуб', 'жук'], contrast: '[з]–[ж]' },
    { pair: ['сук', 'жук'], contrast: '[с]–[ж]' },
    { pair: ['жаба', 'шапка'], contrast: '[ж]–[ш]' },
  ],
  '13-15': [
     { pair: ['чаша', 'чаща'], contrast: '[ш]–[щ]' },
    { pair: ['суп', 'шуба'], contrast: '[с]–[ш]' },
    { pair: ['зуб', 'суп'], contrast: '[з]–[с]' },
    { pair: ['зуб', 'жук'], contrast: '[з]–[ж]' },
    { pair: ['сук', 'жук'], contrast: '[с]–[ж]' },
    { pair: ['жаба', 'шапка'], contrast: '[ж]–[ш]' },
  ],
  '16+': [
     { pair: ['чаша', 'чаща'], contrast: '[ш]–[щ]' },
    { pair: ['суп', 'шуба'], contrast: '[с]–[ш]' },
    { pair: ['зуб', 'суп'], contrast: '[з]–[с]' },
    { pair: ['зуб', 'жук'], contrast: '[з]–[ж]' },
    { pair: ['сук', 'жук'], contrast: '[с]–[ж]' },
    { pair: ['жаба', 'шапка'], contrast: '[ж]–[ш]' },
  ],
};

const PhonemicPerception = ({ ageGroup, onBack, onComplete }) => {
  const { play: playInstruction, stop: stopInstruction } = useInstructionAudio();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [answerLog, setAnswerLog] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasSpoken, setHasSpoken] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [shuffledCards, setShuffledCards] = useState([]);

  const hasSpokenRef = useRef(false);

  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const generateQuestions = () => {
    const pairs = PAIRS_BY_AGE[ageGroup] || PAIRS_BY_AGE['4'];
    return pairs.map(({ pair, contrast }, index) => {
      const [correct, distractor] = pair;
      return {
        id: `q-${index}`,
        correct,
        distractor,
        contrast,
        correctImage: `${S3_BASE_URL}/${IMAGE_MAP[correct]}.png`,
        distractorImage: `${S3_BASE_URL}/${IMAGE_MAP[distractor]}.png`,
      };
    });
  };

  const questions = generateQuestions();
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  useEffect(() => {
    if (currentQuestion && shuffledCards.length === 0) {
      const cards = [
        { key: 'correct', word: currentQuestion.correct, image: currentQuestion.correctImage },
        { key: 'distractor', word: currentQuestion.distractor, image: currentQuestion.distractorImage },
      ];
      setShuffledCards(shuffleArray(cards));
    }
  }, [currentQuestion, currentQuestionIndex, shuffledCards.length]);

  // Базовая функция озвучки слова
  const handleSpeak = useCallback(() => {
    if (!currentQuestion) return;
    stopInstruction();
    setIsSpeaking(true);
    setIsDisabled(true);

    playInstruction('Послушай и выбери', () => {
      playInstruction(currentQuestion.correct, () => {
        setIsSpeaking(false);
        setIsDisabled(false);
      });
    });
  }, [currentQuestion]);

  // Автозапуск озвучки при новом вопросе
  const speakWord = () => {
    if (hasSpokenRef.current) return;
    hasSpokenRef.current = true;
    setHasSpoken(true);
    handleSpeak();
  };

  // Кнопка повтора инструкции
  const repeatInstruction = useCallback(() => {
    handleSpeak();
  }, [handleSpeak]);

  useEffect(() => {
    if (currentQuestion && !showResult && !hasSpoken) {
      const timer = setTimeout(() => speakWord(), 600);
      return () => clearTimeout(timer);
    }
  }, [currentQuestion, currentQuestionIndex, showResult, hasSpoken]);

  const handleCardClick = (card) => {
    if (isDisabled || isSpeaking) return;

    stopInstruction();
    setIsDisabled(true);
    setSelectedCard(card.key);
    const correct = card.key === 'correct';
    setIsCorrect(correct);
    setAnswers((prev) => [...prev, correct]);
    setAnswerLog((prev) => [...prev, {
      contrast: currentQuestion.contrast,
      correctWord: currentQuestion.correct,
      distractorWord: currentQuestion.distractor,
      chosenWord: correct ? currentQuestion.correct : currentQuestion.distractor,
      correct,
    }]);

    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        goToNextQuestion();
      } else {
        setShowResult(true);
      }
    }, 1300);
  };

  const goToNextQuestion = () => {
    setCurrentQuestionIndex((prev) => prev + 1);
    setShuffledCards([]);
    setSelectedCard(null);
    setIsCorrect(null);
    setIsDisabled(false);
    setHasSpoken(false);
    hasSpokenRef.current = false;
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;

        const failedContrasts = [...new Set(answerLog.filter(a => !a.correct).map(a => a.contrast))];
        const okContrasts = [...new Set(answerLog.filter(a => a.correct).map(a => a.contrast))];
        const failedPairs = answerLog
          .filter(a => !a.correct)
          .map(a => ({ pair: `${a.correctWord}–${a.distractorWord}`, chosenWord: a.chosenWord, correctWord: a.correctWord }));

        if (onComplete) {
          onComplete({
            testId: 'PhonemicPerception',
            score,
            details: { total: totalQuestions, correct: correctCount, failedContrasts, okContrasts, failedPairs },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = answers.filter((a) => a === true).length;
    return (
      <ModuleTemplate
        progress={answers}
        totalQuestions={totalQuestions}
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="phonemic-result-content">
          <h2 className="phonemic-result-title">🎉 Упражнение завершено!</h2>
          <div className="phonemic-result-score">
            <span className="phonemic-score-number">{correctCount}</span>
            <span className="phonemic-score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) {
    return (
      <ModuleTemplate
        progress={answers}
        totalQuestions={totalQuestions}
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="phonemic-loading-text">Загрузка упражнений...</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestions}
      forceStop={true}
      onComplete={onComplete || onBack}
      onRepeat={repeatInstruction}
      disabled={isDisabled}
      extraContent="Послушай и выбери"
    >
      <div className="phonemic-container">
        <div className={`phonemic-speaking-badge ${isSpeaking ? 'active' : ''}`}>
          <span className="phonemic-speaking-icon">🔊</span>
          <span className="phonemic-speaking-text">Слушай...</span>
        </div>

        <div className="phonemic-cards-grid">
          {shuffledCards.map((card) => {
            const isSelected = selectedCard === card.key;
            const showCorrect = isSelected && isCorrect === true;
            const showWrong = isSelected && isCorrect === false;
            const showCorrectHint =
              selectedCard === 'distractor' && isCorrect === false && card.key === 'correct';

            return (
              <div
                key={card.key}
                className={`phonemic-card-wrapper ${
                  !isDisabled && !isSpeaking ? 'clickable' : ''
                } ${showCorrect ? 'answer-correct' : ''} ${
                  showWrong ? 'answer-wrong' : ''
                } ${showCorrectHint ? 'answer-correct-hint' : ''}`}
                onClick={() => handleCardClick(card)}
              >
                <img
                  src={card.image}
                  alt=""
                  className="phonemic-card-image"
                  onError={(e) => {
                    e.target.src = '/placeholder.png';
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default PhonemicPerception;